import React, { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import styles from './DatabaseScreen.module.css';
import { Project } from '../../types/api';
import { Loader } from '../../components/Loader';
import { useProjects } from '../../hooks/useProjects';

interface ProjectCardProps {
  project: Project;
  onSelect: (project: Project) => void;
}

const ProjectCard = memo<ProjectCardProps>(({ project, onSelect }) => {
  const [imgSrc, setImgSrc] = React.useState(project.logo || '/assets/default-project-logo.svg');

  const handleImageError = useCallback(() => {
    setImgSrc('/assets/default-project-logo.svg');
  }, [project.logo, project.project_name]);

  const handleClick = useCallback(() => {
    WebApp.HapticFeedback.impactOccurred('light');
    onSelect(project);
  }, [project, onSelect]);

  return (
    <div className={styles.projectCard} onClick={handleClick}>
      <img 
        src={imgSrc}
        alt={project.project_name} 
        className={styles.projectLogo}
        onError={handleImageError}
      />
      <h2 className={styles.projectName}>{project.project_name}</h2>
    </div>
  );
});

ProjectCard.displayName = 'ProjectCard';

const DatabaseScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    projects,
    isInitialLoading,
    isLoadingMore,
    error,
    hasMore,
    totalCount,
    currentPage,
    searchQuery,
    setSearchQuery,
    lastProjectRef
  } = useProjects();

  const handleProjectSelect = useCallback((project: Project) => {
    WebApp.HapticFeedback.impactOccurred('light');
    navigate(`/database/project/${project.project_id}`);
  }, [navigate]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  const renderProjectsList = () => {
    // Show loader in projects container when loading
    if (isInitialLoading) {
      return (
        <div className={styles.loadingContainer}>
          <Loader />
        </div>
      );
    }

    if (projects.length === 0) {
      return (
        <div className={styles.noResults}>
          {searchQuery ? 'No projects found for your search' : 'No projects available'}
        </div>
      );
    }

    return projects.map((project, index) => {
      const isLastProject = projects.length === index + 1;
      
      return (
        <div 
          key={project.project_id} 
          ref={isLastProject ? lastProjectRef : undefined}
        >
          <ProjectCard 
            project={project}
            onSelect={handleProjectSelect}
          />
        </div>
      );
    });
  };

  return (
    <div className={styles.databaseScreen}>
      <div className={styles.searchContainer}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search project..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
        {totalCount > 0 && (
          <div className={styles.totalCount}>
            Found: {totalCount.toLocaleString()} projects {currentPage > 1 && `(Page ${currentPage})`}
          </div>
        )}
      </div>
      
      <div className={styles.projectsList}>
        {renderProjectsList()}
      </div>
      
      {isLoadingMore && (
        <div className={styles.loadingMore}>
          <Loader />
        </div>
      )}
      
      {error && (
        <div className={styles.error}>
          <p>Error: {error}</p>
          <button 
            className={styles.retryButton}
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      )}
      
      {!hasMore && projects.length > 0 && !isLoadingMore && (
        <div className={styles.noMore}>
          No more projects to load
        </div>
      )}
    </div>
  );
};

export default DatabaseScreen; 